import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface GenderPayGapCalculatorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUseValue: (value: string) => void;
}

export function GenderPayGapCalculatorModal({
  open,
  onOpenChange,
  onUseValue,
}: GenderPayGapCalculatorModalProps) {
  const [malePay, setMalePay] = useState("");
  const [femalePay, setFemalePay] = useState("");

  const male = parseFloat(malePay);
  const female = parseFloat(femalePay);
  const result =
    !isNaN(male) && !isNaN(female) && male > 0
      ? (((male - female) / male) * 100).toFixed(1)
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Calculate Gender Pay Gap</DialogTitle>
          <DialogDescription className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            A positive value means men are paid more on average than women. A
            negative value means women are paid more. The result is expressed as
            a percentage of average male pay. Payment data entered here is not
            saved.
          </DialogDescription>
        </DialogHeader>

        <p className="font-mono text-sm bg-gray-50 border rounded p-2">
          [(Male avg hourly pay − Female avg hourly pay) ÷ Male avg hourly pay]
          × 100
        </p>

        <div className="flex flex-col gap-2">
          <Label>Average gross hourly pay — Male</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={malePay}
            onChange={(e) => setMalePay(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label>Average gross hourly pay — Female</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={femalePay}
            onChange={(e) => setFemalePay(e.target.value)}
          />
        </div>

        {result !== null && (
          <p className="text-sm font-medium text-green-700">
            Calculated Gender Pay Gap: {result}%
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setMalePay("");
              setFemalePay("");
            }}
          >
            Clear
          </Button>
          {/* Non-null assertion safe: button is disabled when result is null */}
          <Button
            disabled={result === null}
            onClick={() => onUseValue(result!)}
          >
            Use this value
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
